import type React from 'react'
import {
  BookOpen,
  Columns,
  Image as ImageIcon,
  LayoutTemplate,
  Newspaper,
  PanelRightClose,
  PanelRightOpen,
  SquareUser,
  Sticker,
} from 'lucide-react'
import { cn } from '@/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { getVersionInfo } from '@/utils/version'
import { RIGHT_PANEL_IDS } from '../constants/panel-ids'
import { ResetCheckpointButton } from './reset-checkpoint-button'

const RAIL_ITEMS = [
  { key: RIGHT_PANEL_IDS.spreads, Icon: BookOpen, label: 'Spreads' },
  { key: RIGHT_PANEL_IDS.photos, Icon: ImageIcon, label: 'Photos' },
  { key: RIGHT_PANEL_IDS.portraits, Icon: SquareUser, label: 'Portraits' },
  { key: RIGHT_PANEL_IDS.clikart, Icon: Sticker, label: 'ClikArt' },
  { key: RIGHT_PANEL_IDS.templates, Icon: LayoutTemplate, label: 'Templates' },
  { key: RIGHT_PANEL_IDS.backgrounds, Icon: Columns, label: 'Backgrounds' },
  { key: RIGHT_PANEL_IDS.adorders, Icon: Newspaper, label: 'Ad Orders' },
]

interface PanelRailProps {
  isOpen: boolean
  activePanel: string | null
  spreadId: string | null
  /** Filled with each icon button, keyed by panel id (Escape chains return focus to them). */
  railButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>
  onToggle: () => void
  onSelectPanel: (panel: string) => void
}

/** The always-visible right icon rail: open/close toggle, one icon per panel, dev reset button. */
export function PanelRail({
  isOpen,
  activePanel,
  spreadId,
  railButtonRefs,
  onToggle,
  onSelectPanel,
}: PanelRailProps) {
  return (
    <TooltipProvider>
      <div
        className="w-12 flex flex-col items-start border-l border-border bg-sidebar-background z-50"
        data-right-panel-rail="true"
      >
        {/* Toggle button */}
        <div className="flex flex-col gap-2 p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={onToggle}
                className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
                data-state={isOpen ? 'active' : 'inactive'}
                aria-pressed={isOpen}
                aria-label={isOpen ? 'Close right panel' : 'Open right panel'}
              >
                {isOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{isOpen ? 'Collapse Panel' : 'Expand Panel'}</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="flex flex-1 flex-col gap-2 p-2">
          {/* Tool icons */}
          {RAIL_ITEMS.map(({ key, Icon, label }) => {
            const active = activePanel === key && isOpen

            return (
              <Tooltip key={key}>
                <TooltipTrigger asChild>
                  <Button
                    ref={(el) => {
                      railButtonRefs.current[key] = el
                    }}
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => onSelectPanel(key)}
                    className={cn(
                      active
                        ? 'bg-accent text-accent-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    )}
                    aria-pressed={active}
                    aria-label={label}
                  >
                    <Icon size={16} className={cn('text-current', active && 'font-semibold')} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{label}</p>
                </TooltipContent>
              </Tooltip>
            )
          })}

          {/* Dev-only reset button */}
          {getVersionInfo().isLocal && (
            <>
              <div className="my-1 w-full border-t border-border" />
              <ResetCheckpointButton spreadId={spreadId} />
            </>
          )}
        </div>
      </div>
    </TooltipProvider>
  )
}
